import React, { useState } from 'react';
import {
  Button,
  FormSelect,
  FormInput,
  InlineEditColumnProps,
  InlineEditTable,
  Form,
  MasterDetailForm,
  Widget,
  FormatText,
  FormSubmitButton,
} from '@digital/ui';

import * as Yup from 'yup';
import { Collapse, Icon, Card } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { hasPermission } from '@app/helpers/permissions';

import { CrfAcronimoProjeto } from '@app/api/crf/acronimo/projeto/crfAcronimosProjeto';

interface TipoProjetoProps {
  acronimosProjeto: CrfAcronimoProjeto[];
  categorias: { text: string; value: string }[] | undefined;
  grupos: { text: string; value: string }[] | undefined;
  handleDelete(id: string): Promise<unknown>;
  handleCreate: (record: CrfAcronimoProjeto) => Promise<CrfAcronimoProjeto | undefined>;
  handleUpdate: (record: CrfAcronimoProjeto) => Promise<boolean>;
  shouldUpdate: (needed: boolean) => void;
  isUpdate: boolean;
}

const initialState: CrfAcronimoProjeto = {
  id: '',
  sigla: '',
  nomeCategoria: '',
  categoriaId: '',
  nomeGrupo: '',
  grupoId: '',
};

const CRFAcronimoProjetoTable = ({
  acronimosProjeto,
  categorias,
  grupos,
  isUpdate,
  handleCreate,
  handleUpdate,
  handleDelete,
  shouldUpdate,
}: TipoProjetoProps) => {

  const [acronimo, setAcronimoProjeto] = useState<CrfAcronimoProjeto>(initialState);

  const validationSchema = Yup.object().shape({
    sigla: Yup.string().required('Sigla deve ser informada'),
    categoriaId: Yup.string().required('Categoria deve ser informada'),
    grupoId: Yup.string().required('Grupo deve ser informado'),
  });

  const customColumns = () => {
    return [
      {
        title: 'Sigla',
        dataIndex: 'sigla',
        width: '20%',
        renderEdit: () => <FormInput name="sigla" disabled={false} required />,
      },
      {
        title: 'Categoria',
        dataIndex: 'categoriaId',
        key: 'categoriaId',
        options: categorias,
        width: '20%',
        render: (_: string, record: CrfAcronimoProjeto) => {
          const option = categorias && categorias.find(f => f.value === record.categoriaId);
          return option ? option.text : '';
        },
        renderEdit: () => <FormSelect name="categoriaId" options={categorias} required disabled={false} />,
      },
      {
        title: 'Grupo',
        dataIndex: 'grupoId',
        key: 'grupoId',
        options: grupos,
        width: '30%',
        render: (_: string, record: CrfAcronimoProjeto) => {
          const option = grupos && grupos.find(f => f.value === record.grupoId);
          return option ? option.text : '';
        },
        renderEdit: () => <FormSelect name="grupoId" options={grupos} required disabled={false} />,
      },
      {
        render: (item: CrfAcronimoProjeto) => (
          <>
            <Button
              confirm="Deseja realmente excluir este registro?"
              okText="Confirmar"
              onClick={() => handleDelete(item.id).then(() => {
                shouldUpdate(!isUpdate);
              })}
              icon="science-minus"
              style={{ width: 43, margin: 4, textAlign: 'center' }}
            />
          </>
        ),
      },
    ] as InlineEditColumnProps<CrfAcronimoProjeto>[];
  };

  const handleChangeSigla = (sigla: string) => {
    if (sigla.length > 0 && acronimo) {
      setAcronimoProjeto({ ...acronimo, sigla });
    }
  };

  const handleChangeCategoria = (categoria: SelectValue) => {
    if (categoria.valueOf && acronimo) {
      setAcronimoProjeto({ ...acronimo, categoriaId: categoria.toString() });
    }
  };

  const handleChangeGrupo = (grupo: SelectValue) => {
    if (grupo.valueOf && acronimo) {
      setAcronimoProjeto({ ...acronimo, grupoId: grupo.toString() });
    }
  };

  const handleClick = async () => {
    if (acronimo) {
      const result = await handleCreate(acronimo);
      if (result) {
        !isUpdate ? shouldUpdate(true) : shouldUpdate(false);
      }
    }
  };

  const { Panel } = Collapse;

  return (
    <>
      <Collapse bordered={false} defaultActiveKey={['2']} expandIcon={() => <Icon type="arrows-right_4" />}>
        <Panel key="2" header="Acrônimos Projeto" style={{ border: 0 }}>
          <MasterDetailForm validationSchema={validationSchema} initialValues={initialState}>
            <Widget
              actions={[
                hasPermission(['ACRONIMOS_CADASTRAR', 'ACRONIMOS_EDITAR']) && (
                  <FormSubmitButton type="primary" onClick={handleClick}>
                    <FormatText id="ui.commons.save" uppercase />
                  </FormSubmitButton>
                ),
              ]}>
              <InlineEditTable
                className="removeScrollbar"
                rowKey="id"
                columns={customColumns()}
                updateItem={handleUpdate}
                dataSource={acronimosProjeto}
                pagination={false}
                disablenotification={false}
              />
              <fieldset>
                <legend>Inserir Nova Linha</legend>
                <FormInput label="Sigla" name="sigla" span={3} onChange={e => handleChangeSigla(e.target.value)} />
                <FormSelect
                  showSearch
                  label="Categoria"
                  name="categoriaId"
                  options={categorias}
                  span={3}
                  optionFilterProp="children"
                  filterOption={(input, item) => {
                    return item.props.children.props.id
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  onSelect={e => handleChangeCategoria(e)}
                />
                <FormSelect
                  showSearch
                  label="Grupo"
                  name="grupoId"
                  options={grupos}
                  span={3}
                  optionFilterProp="children"
                  filterOption={(input, item) => {
                    return item.props.children.props.id
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  onSelect={e => handleChangeGrupo(e)}
                />
              </fieldset>
            </Widget>
          </MasterDetailForm>
        </Panel>
      </Collapse>
    </>
  );
};

export default CRFAcronimoProjetoTable;
